import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect, useSelector, useDispatch } from 'react-redux';
import Link from 'react-router/lib/Link';
import get from 'lodash/get';
import cn from 'classnames';
import browserHistory from 'react-router/lib/browserHistory';

import Button from '../Button';
import TextInput from '../TextInput';
import Loader from '../common/Loader';
import ErrorBlock from '../common/ErrorBlock';
import PasswordInput from '../TextInput/PasswordInput';
import brandConfig, { isFlagler } from '../../constants/brandConfig';
import { LOGIN_ERRORS, LOGIN_INVITATION_ERRORS } from '../../constants/errors';
import { defaultValidator } from '../../utils/validations/defaultValidator';
import * as sendUserInvitation from '../../redux/auth/send-invitation-req';
import { AUTH } from '../../constants/strings';
import * as login from '../../redux/auth/login';
import { actions as authAzureActions } from '../../redux/auth/authAzure';
import { PATTERNS } from '../../constants/validations';
import {
  isLoggedInSelector,
  selectAuthLogoutError,
  selectSendInvitationError,
  selectSendInvitationSucceed,
  selectSendInvitationPending,
} from '../../redux/auth/selectors';
import SimpleInfoPopup from '../common/SimpleInfoPopup';
import { useToggleFlag } from '../../hocs/useToogleFlag';
import MfaDialog from '../MfaDialog';
import {
  RESET_PASSWORD,
  SIGNUP_INVITATION,
  LOGIN,
} from '../../constants/routes';
import { actions as mfaConfirmationsActions } from '../../redux/profiles/mfa-confirmations';
import { actions as loginAzureActions } from '../../redux/auth/login-azure';
import { actions as logoutActions } from '../../redux/auth/logout';
import { getMfaConfirmationData } from '../../redux/profiles/selectors';
import { actions as publicConstantsActions } from '../../redux/data/publicConstants';
import { actions as authAzureExternalRedirectActions } from '../../redux/auth/authAzureExternalRedirect';
import parseJSONSafely from '../../utils/parseJSONSafely';
import Icon from '../Icon';
import styles from './index.styles';
import MetaHelmet from '../AppHelmet/MetaHelmet';
import { usePageView } from '../../hocs/usePageView';
import OtpModal from '../OtpModal';
import { selectIsOtpEnabled } from '../../redux/data/selectors';

const HTTP_STATUS_LOCKED = 423;

const Login = ({
  getAction,
  rejected,
  send,
  clear,
  error,
  expiredError,
  sending,
  sendInvitation,
  clearInvitation,
  clearLogoutAction,
  sendInvitationError,
  sendInvitationSucceed,
  sendInvitationPending,
  getMfaConfirmtation,
  clearMfaConfirmtation,
  clearMfaCredential,
  intl: { formatMessage },
  serializedForm,
  loginForMFA,
  token,
}) => {
  const [isErrored, showError] = useState(false);
  const [savedLogin, setLogin] = useState(null);
  const [opened, openConfirm, closeConfirm] = useToggleFlag();
  const [loginError, setLoginError] = useState('');
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaError, setMfaError] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isAnyOfOtpPathModalsOpen, setIsAnyOfOtpPathModalsOpen] = useState(
    false,
  );

  const [showOtpLink, setShowOtpLink] = useState(false);

  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(window.location.search);

  const type = searchParams.get('type');

  const codeSearchParam = searchParams.get('code');

  const loginSearchParam = searchParams.get('login');

  const tokenSearchParam = searchParams.get('token');

  const {
    data: mfaConfirmationData,
    sending: mfaConfirmationLoading,
  } = useSelector(getMfaConfirmationData);

  const isOtpEnabled = useSelector(selectIsOtpEnabled);

  usePageView('Login');
  useEffect(() => {
    if (rejected && !isAnyOfOtpPathModalsOpen) {
      const code = get(error, 'code');
      const status = get(error, 'status');
      if (status === HTTP_STATUS_LOCKED) {
        showError(false);
        if (!mfaOpen) {
          handleSendMfaCode();
        } else {
          setMfaError(code);
        }
      } else if (LOGIN_INVITATION_ERRORS.includes(code)) {
        showError(false);
        openConfirm();
      } else {
        showError(true);
      }
    } else {
      showError(false);
    }
  }, [rejected, error, loginForMFA, isAnyOfOtpPathModalsOpen]);

  useEffect(() => {
    if (mfaConfirmationData) {
      setMfaOpen(true);
    }
  }, [mfaConfirmationData]);

  const isEmail = value =>
    defaultValidator(PATTERNS.HAS_ALPHABETICAL_CHARACTER, value);

  const errorTextId = useMemo(() => {
    const code = get(error, 'code');
    return LOGIN_ERRORS[code] || 'errors.login.unauthorized';
  }, [error]);

  const invitationTextIDs = useMemo(() => {
    return isEmail(savedLogin)
      ? {
          messageId: 'auth.info.inviteEmailNotConfirmed',
          titleId: 'auth.info.title.inviteEmailNotConfirmed',
        }
      : {
          messageId: 'auth.info.invitePhoneNotConfirmed',
          titleId: 'auth.info.title.invitePhoneNotConfirmed',
        };
  }, [savedLogin]);

  const headingTextId = expiredError
    ? 'auth.expire.expiredSession'
    : 'auth.login.text';

  const resetReqError = () => {
    if (error) clear();
  };

  const resetLoginError = () => {
    if (loginError) setLoginError('');
    resetReqError();
  };

  const handleInvitation = () =>
    sendInvitation({ user: { login: savedLogin } });

  const handleCloseConfirm = () => {
    clearInvitation();
    closeConfirm();
    setLogin('');
    clearMfaCredential();
  };

  const handleSendMfaCode = () => {
    getMfaConfirmtation({ login: savedLogin || loginForMFA });
  };

  const handleMfaLogin = code => {
    const data = {
      ...serializedForm,
      mfa_code: code,
    };
    if (loginForMFA) {
      dispatch(loginAzureActions.send({ mfa_code: code, access_token: token }));
    } else {
      send(data);
    }
  };

  const handleMfaClose = () => {
    setMfaOpen(false);
    setMfaError(false);
    clearMfaConfirmtation();
    clearMfaCredential();
  };

  getAction(data => {
    setShowOtpLink(isOtpEnabled);
    clearLogoutAction();

    const mfaFingerprint = get(
      parseJSONSafely(localStorage.getItem('user')),
      'mfaFingerprint',
    );
    const user = { ...data };
    if (mfaFingerprint) {
      user.mfa_fingerprint = mfaFingerprint;
    }

    const userLogin = get(data, 'login', null);

    if (!sending) {
      setLogin(userLogin);
      send(user);
    }
  });

  const handleLoginAzure = e => {
    e.preventDefault();
    resetReqError();
    clearMfaConfirmtation();
    dispatch(authAzureActions.init({ intl: formatMessage }));
  };

  const handleSendOTPLinkClick = () => {
    clear();
    setShowOtpModal(true);
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
  };

  useEffect(() => {
    if (isOtpEnabled === undefined) {
      dispatch(publicConstantsActions.send());
    }
  }, [isOtpEnabled]);

  useEffect(() => {
    if (type) {
      browserHistory.push(LOGIN);
      dispatch(
        authAzureExternalRedirectActions.init({
          type,
          code: codeSearchParam,
          intl: formatMessage,
          login: loginSearchParam,
          token: tokenSearchParam,
        }),
      );
    }
  }, []);

  const [valueForms, setFormValues] = useState({});

  const checkFormValues = (name, value) => {
    setFormValues(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const isDisabledSubmit = useMemo(() => {
    const isFormFilled = valueForms.password;

    return sending || mfaConfirmationLoading || !isFormFilled;
  }, [sending, mfaConfirmationLoading, valueForms]);

  return (
    <div className={cn(styles.formWrapper, 'relative')}>
      <MetaHelmet title="pages.title.login" />

      {sending && <Loader className="absolute-center z-999" />}
      <p className={styles.formTitle}>
        <FormattedMessage {...AUTH.titles.login} />
      </p>
      {brandConfig.welcomeText && (
        <p className={styles.welcomeText}>{brandConfig.welcomeText}</p>
      )}
      <div className={styles.formHeading}>
        {formatMessage({
          id: isFlagler ? 'auth.login.flagler.text' : headingTextId,
        })}
      </div>
      <TextInput
        className={cn(styles.labelField, styles.inputField, 'mb3')}
        id="login-login"
        label={formatMessage(AUTH.fields.login)}
        name="login"
        placeholder={formatMessage(AUTH.fields.loginPlaceholder)}
        required
        rounded
        onBlur={val => {
          checkFormValues('login', val);
        }}
        onFocus={resetLoginError}
      />
      <PasswordInput
        className={cn(styles.labelField, styles.inputField, 'mb3')}
        id="login-password"
        autoComplete="new-password"
        required
        rounded
        onFocus={resetReqError}
        onBlur={val => {
          checkFormValues('password', val);
        }}
      />
      {isErrored && (
        <ErrorBlock
          errors={formatMessage({ id: errorTextId })}
          className={cn(styles.errorBlock, 'mt3')}
        />
      )}
      <div className={styles.links}>
        <div
          onClick={handleSendOTPLinkClick}
          className={cn(
            styles.link,
            {
              [styles.forgotPassBtn]: !isFlagler,
              [styles.recoverLink]: isFlagler,
              [styles.hidden]: !isOtpEnabled && !showOtpLink,
            },
            { mt3: isErrored },
          )}
        >
          <FormattedMessage {...AUTH.actions.otp.textLink} />
        </div>

        <div
          className={cn(
            styles.link,
            {
              [styles.forgotPassBtn]: !isFlagler,
              [styles.recoverLink]: isFlagler,
            },
            { mt3: isErrored },
          )}
        >
          <Link to={`/${RESET_PASSWORD}`}>
            <FormattedMessage {...AUTH.actions.recoverPassword.textLink} />
          </Link>
        </div>
      </div>

      <Button
        className={cn(styles.acceptBtn, 'db mb3 w-100 lh-copy')}
        color="primary"
        raised
        type="submit"
        disabled={isDisabledSubmit}
      >
        <FormattedMessage {...AUTH.submit.signin} />
      </Button>

      <div className={styles.loginAzureWrapper}>
        <div className={styles.dividerOr}>
          {formatMessage({ id: 'auth.otherLoginOptions' })}
        </div>
        <Button
          className={cn(
            styles.acceptBtn,
            styles.microsoftButton,
            'db mb3 w-100 lh-copy',
          )}
          color="accent"
          raised
          onClick={handleLoginAzure}
          disabled={sending || mfaConfirmationLoading}
        >
          <Icon
            name="icon-microsoft"
            size={24}
            className={styles.microsoftLogo}
          />
          <FormattedMessage {...AUTH.submit.loginAzure} />
        </Button>
      </div>

      <p className={cn(styles.signupLink, { mb4: isFlagler }, 'pt0')}>
        <FormattedMessage id={brandConfig.registrationLinkTitle} />
        <Link className={cn(styles.link, 'ml1')} to={`/${SIGNUP_INVITATION}`}>
          <FormattedMessage id="submit.registerHere" />
        </Link>
      </p>

      <SimpleInfoPopup
        {...invitationTextIDs}
        icon="icon-plane"
        opened={opened}
        width="rubber"
        withHeading={false}
        mobileFullScreen={false}
        dialogClassName="w-100"
        autoHeight
      >
        {sendInvitationError && (
          <ErrorBlock
            errors={formatMessage({ id: 'errors.common' })}
            className={cn(styles.errorBlock, 'mt3')}
          />
        )}
        <div className="df justify-center mt4">
          <Button
            onClick={handleInvitation}
            color="primary"
            raised
            disabled={sendInvitationPending || sendInvitationSucceed}
            className="w-50"
          >
            {formatMessage({ id: 'common.resendInvite' })}
          </Button>
          <Button
            onClick={handleCloseConfirm}
            color="primary"
            raised
            className="ml2 w-50"
          >
            {formatMessage({ id: 'submit.expiredToken' })}
          </Button>
        </div>
      </SimpleInfoPopup>

      {mfaOpen && (
        <MfaDialog
          open
          title={formatMessage({ id: 'mfa.dialog.title' })}
          mfaData={mfaConfirmationData}
          onClose={handleMfaClose}
          onResend={handleSendMfaCode}
          onSubmit={handleMfaLogin}
          error={mfaError}
          loading={sending}
        />
      )}

      <OtpModal
        open={showOtpModal}
        formatMessage={formatMessage}
        onClose={handleCloseOtpModal}
        setIsAnyOfOtpPathModalsOpen={setIsAnyOfOtpPathModalsOpen}
      />
    </div>
  );
};

Login.propTypes = {
  getAction: PropTypes.func.isRequired,
  clearLogoutAction: PropTypes.func,
  loggedIn: PropTypes.bool.isRequired,
  rejected: PropTypes.bool.isRequired,
  send: PropTypes.func.isRequired,
  clear: PropTypes.func.isRequired,
  error: PropTypes.object,
  succeed: PropTypes.bool.isRequired,
  sending: PropTypes.bool.isRequired,
  expiredError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  sendInvitation: PropTypes.func,
  clearInvitation: PropTypes.func,
  sendInvitationError: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  sendInvitationSucceed: PropTypes.bool,
  sendInvitationPending: PropTypes.bool,
  getMfaConfirmtation: PropTypes.func,
  clearMfaConfirmtation: PropTypes.func,
  serializedForm: PropTypes.object,
  clearMfaCredential: PropTypes.func,
  loginForMFA: PropTypes.string,
  token: PropTypes.string,
};

Login.defaultProps = {
  error: null,
};

const mapStateToProps = state => ({
  loggedIn: isLoggedInSelector(state),
  rejected: !!state.auth.login.rejected || !!state.auth.loginAzure.rejected,
  sending: !!state.auth.login.sending || !!state.auth.loginAzure.sending,
  error: state.auth.login.error || state.auth.loginAzure.error,
  succeed: !!state.auth.login.succeed || !!state.auth.loginAzure.succeed,
  expiredError: selectAuthLogoutError(state),
  sendInvitationSucceed: selectSendInvitationSucceed(state),
  sendInvitationPending: selectSendInvitationPending(state),
  sendInvitationError: selectSendInvitationError(state),
  loginForMFA: state.auth.loginAzure.loginMFA,
  token: state.auth.loginAzure.token,
});

const mapDispatchToProps = {
  send: login.actions.send,
  clear: login.actions.clear,
  clearLogoutAction: logoutActions.clear,
  clearMfaCredential: loginAzureActions.clear,
  sendInvitation: sendUserInvitation.actions.send,
  clearInvitation: sendUserInvitation.actions.clear,
  getMfaConfirmtation: mfaConfirmationsActions.send,
  clearMfaConfirmtation: mfaConfirmationsActions.clear,
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(Login),
);



// WEBPACK FOOTER //
// ./src/components/Auth/Login.jsx